import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import {
    ButtonModule,
    CheckboxModule,
    FileDropModule,
    InputModule,
    RadioModule,
    SelectModule,
    TextareaModule
} from '@sag-ui';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OrderEntryDomesticFormGroup } from '../../../models/cod-create.model';
import { OrderEntryIssueTypeDomestic, AccountErrorType } from '@cod-monorepo/interfaces';
import { AttachmentsInputComponent } from '../../../../shared/components/attachments-input/attachments-input.component';
import { AddCurrentCodLine, AddErrorMessage, RemoveErrorMessage } from '../../../state/cod.action';
import { Store } from '@ngxs/store';
import { filter, startWith, tap } from 'rxjs';
import { WorkflowError } from '../../../../shared/enums/workflow-error';
import { CsrResponsibleSelectComponent } from '../../../../shared/components/csr-responsible-select/csr-responsible-select.component';
import { ManagerSelectComponent } from '../../../../shared/components/manager-select/manager-select.component';

@Component({
    selector: 'app-cod-order-entry-domestic-step',
    templateUrl: './order-entry-domestic.component.html',
    imports: [
        CommonModule,
        SelectModule,
        InputModule,
        FormsModule,
        ReactiveFormsModule,
        RadioModule,
        CheckboxModule,
        ButtonModule,
        FileDropModule,
        TextareaModule,
        AttachmentsInputComponent,
        CsrResponsibleSelectComponent,
        ManagerSelectComponent
    ],
    standalone: true
})
export class CodOrderEntryDomesticStepComponent implements OnInit {
    @Input() orderEntryDomesticForm: FormGroup<OrderEntryDomesticFormGroup> | undefined;

    private relatedCallTrackingNumberTouched = false;
    public showCsrResponsibleSelect = false;
    public orderEntryIssueTypes = OrderEntryIssueTypeDomestic;
    public accountErrorTypes = AccountErrorType;

    private csrHidden = [
        OrderEntryIssueTypeDomestic.CUSTOMER_ORDER_ERROR_DOM,
        OrderEntryIssueTypeDomestic.EDI_ORDERING_ISSUE_DOM,
        OrderEntryIssueTypeDomestic.MISINFORMATION_PROVIDED_COMMUNICATION_INADEQUATE_DOM
    ];

    constructor(private store: Store) { }

    ngOnInit(): void {
        this.initFinancialAdjustmentChangesListener();
        this.initCallTagTrackingNumberTouchedListener();
        this.initRelatedDocumentsListener();
        this.initCsrResponsibleSelectVisibility();
    }

    public handleConfirm() {
        if (this.orderEntryDomesticForm?.valid) {
            this.store.dispatch(new AddCurrentCodLine());
        }
    }

    public isValid(): boolean {
        return this.orderEntryDomesticForm?.valid ?? false;
    }

    private initFinancialAdjustmentChangesListener(): void {
        this.orderEntryDomesticForm?.controls.financialAdjustment.valueChanges
            .pipe(
                filter(value => value === false),
                tap(() => {
                    this.orderEntryDomesticForm?.controls.adjustmentAmount.setValue(0);
                    this.orderEntryDomesticForm?.controls.filedWithin30Days.setValue(null);
                    this.orderEntryDomesticForm?.controls.managerApprovedRequest.setValue(null);
                }))
            .subscribe();
    }

    private initCallTagTrackingNumberTouchedListener(): void {
        this.orderEntryDomesticForm?.controls.callTagTrackingNumber.valueChanges
            .pipe(
                filter(() => !this.relatedCallTrackingNumberTouched),
                tap(() => {
                    this.store.dispatch(new AddErrorMessage(WorkflowError.UPLOAD_PROOF));

                    this.relatedCallTrackingNumberTouched = true;
                })
            )
            .subscribe();
    }

    private initRelatedDocumentsListener(): void {
        this.orderEntryDomesticForm?.controls.relatedDocuments.valueChanges
            .pipe(
                filter(value => !!value && value.length > 0),
                tap(() => this.store.dispatch(new RemoveErrorMessage(WorkflowError.UPLOAD_PROOF)))
            )
            .subscribe();
    }

    private initCsrResponsibleSelectVisibility(): void {
        this.orderEntryDomesticForm?.controls.issueType.valueChanges
            .pipe(
                startWith(this.orderEntryDomesticForm?.controls.issueType.value),
                filter(value => !!value),
                tap(value => {
                    this.showCsrResponsibleSelect = !this.csrHidden.includes(value);
                })
            )
            .subscribe();
    }
}
